import React, { useState } from 'react';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { apiService } from '../../services/api';
import { isNativePlatform } from '../../utils/platform';

const PREMIUM_FEATURES = [
  { icon: '👥', title: 'Workspaces & Family Sharing', desc: 'Share tasks and plans with family or your team' },
  { icon: '✈️', title: 'Travel Planner', desc: 'Itineraries, documents and AI trip help' },
  { icon: '🤖', title: 'AI Assistant', desc: 'Plan your day and get smart suggestions' },
  { icon: '📧', title: 'Weekly Recap Email', desc: 'A shareable Sunday summary in your inbox' },
  { icon: '📍', title: 'Location Reminders', desc: 'Get nudged when you reach a place' },
  { icon: '⚖️', title: 'Weight Tracker', desc: 'Track weight trends over time' },
];

const FREE_FEATURES = ['Daily & recurring habits', 'Planner & calendar', 'Analytics', 'Meals, cleaning, goals', 'Achievements & streaks'];

const UpgradePage: React.FC = () => {
  const { isPremium, prices, plan: currentPlan, premiumUntil, refresh } = useSubscription();
  const [selected, setSelected] = useState<'monthly' | 'yearly'>('yearly');
  const [busy, setBusy] = useState(false);

  const fmt = (v: number) => `₹${v.toLocaleString('en-IN')}`;
  const yearlyMonthly = Math.round(prices.yearly / 12);
  const savePct = Math.round((1 - prices.yearly / (prices.monthly * 12)) * 100);

  const loadRazorpayScript = (): Promise<boolean> => new Promise((resolve) => {
    if ((window as any).Razorpay) return resolve(true);
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

  const handleSubscribe = async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (isNativePlatform()) {
        const cap = (window as any).Capacitor;
        const rc = cap?.Plugins?.Purchases;
        if (!rc) {
          alert('In-app purchases will be available in the installed app build. On the web you can subscribe with card.');
          setBusy(false);
          return;
        }
        // Native purchase is wired via RevenueCat in the app build (see MOBILE_SETUP.md)
        const offerings = await rc.getOfferings();
        const pkg = offerings?.current?.availablePackages?.find((p: any) =>
          selected === 'yearly' ? /year|annual/i.test(p.identifier || p.product?.identifier || '') : /month/i.test(p.identifier || p.product?.identifier || '')
        ) || offerings?.current?.availablePackages?.[0];
        if (!pkg) { alert('No subscription products found. Please try again later.'); setBusy(false); return; }
        await rc.purchasePackage({ aPackage: pkg });
        await refresh();
        alert('You are now Premium! 🎉');
      } else {
        // Web (India): Razorpay Checkout for the subscription
        const ok = await loadRazorpayScript();
        if (!ok) { alert('Could not load the payment gateway. Please retry.'); setBusy(false); return; }
        const res = await apiService.createRazorpaySubscription(selected);
        if (!res.success || !res.subscriptionId) { alert(res.error || 'Could not start subscription.'); setBusy(false); return; }
        const options: any = {
          key: res.keyId,
          subscription_id: res.subscriptionId,
          name: 'Yojana',
          description: `Premium ${selected === 'yearly' ? 'Yearly' : 'Monthly'}`,
          theme: { color: '#667eea' },
          handler: async (r: any) => {
            const v = await apiService.verifyRazorpaySubscription({ ...r, plan: selected });
            if (v.success) { await refresh(); alert('🎉 Welcome to Yojana Premium! All features are unlocked.'); }
            else alert('Payment received but could not be verified. Please contact support.');
          },
          modal: { ondismiss: () => setBusy(false) }
        };
        const rzp = new (window as any).Razorpay(options);
        rzp.on('payment.failed', (resp: any) => alert('Payment failed: ' + (resp?.error?.description || 'please try again')));
        rzp.open();
        setBusy(false);
      }
    } catch (e: any) {
      alert(e?.response?.data?.error || e?.message || 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  };

  if (isPremium) {
    return (
      <div data-testid="upgrade-page" style={{ padding: '40px 24px', textAlign: 'center' }}>
        <div style={{ maxWidth: '480px', margin: '0 auto', background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', borderRadius: '20px', padding: '40px 28px', color: 'white' }}>
          <div style={{ fontSize: '3rem' }}>✨</div>
          <h2 style={{ margin: '8px 0 0 0', fontSize: '1.6rem', fontWeight: 800 }}>You're a Yojana Premium member</h2>
          <p style={{ margin: '12px 0 0 0', opacity: 0.92 }}>
            Plan: <strong style={{ textTransform: 'capitalize' }}>{currentPlan || 'Premium'}</strong>
            {premiumUntil ? ` · Renews ${new Date(premiumUntil).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}` : ''}
          </p>
          <p style={{ margin: '18px 0 0 0', fontSize: '0.85rem', opacity: 0.85 }}>Manage or cancel anytime from your app store or billing portal.</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="upgrade-page" style={{ padding: '32px 24px', maxWidth: '900px', margin: '0 auto' }}>
      <div style={{ textAlign: 'center', marginBottom: '28px' }}>
        <h1 style={{ margin: 0, fontSize: '2rem', fontWeight: 800, color: '#1f2937' }}>Go Premium</h1>
        <p style={{ margin: '8px 0 0 0', color: '#6b7280' }}>Unlock the full power of Yojana. Cancel anytime.</p>
      </div>

      {/* Plan toggle */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: '14px', marginBottom: '24px', flexWrap: 'wrap' }}>
        {(['monthly', 'yearly'] as const).map(p => (
          <button
            key={p}
            onClick={() => setSelected(p)}
            data-testid={`plan-${p}`}
            style={{
              position: 'relative',
              minWidth: '190px',
              padding: '18px 20px',
              borderRadius: '16px',
              border: selected === p ? '2px solid #667eea' : '2px solid #e5e7eb',
              backgroundColor: selected === p ? '#eef2ff' : 'white',
              cursor: 'pointer',
              textAlign: 'left'
            }}
          >
            <div style={{ fontSize: '0.8rem', color: '#6b7280', textTransform: 'capitalize', fontWeight: 600 }}>{p}</div>
            <div style={{ fontSize: '1.6rem', fontWeight: 800, color: '#1f2937' }}>
              {p === 'monthly' ? `${fmt(prices.monthly)}` : `${fmt(prices.yearly)}`}
              <span style={{ fontSize: '0.85rem', fontWeight: 500, color: '#6b7280' }}>{p === 'monthly' ? '/mo' : '/yr'}</span>
            </div>
            {p === 'yearly' && (
              <div style={{ fontSize: '0.75rem', color: '#059669', fontWeight: 700, marginTop: '2px' }}>
                ≈ {fmt(yearlyMonthly)}/mo · save {savePct}%
              </div>
            )}
            {p === 'yearly' && savePct > 0 && (
              <span style={{ position: 'absolute', top: '-10px', right: '12px', backgroundColor: '#059669', color: 'white', fontSize: '0.65rem', fontWeight: 700, padding: '3px 8px', borderRadius: '10px' }}>BEST VALUE</span>
            )}
          </button>
        ))}
      </div>

      <div style={{ textAlign: 'center', marginBottom: '30px' }}>
        <button
          onClick={handleSubscribe}
          disabled={busy}
          data-testid="subscribe-btn"
          style={{ padding: '15px 44px', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', border: 'none', borderRadius: '14px', fontWeight: 800, fontSize: '1.05rem', cursor: busy ? 'wait' : 'pointer', boxShadow: '0 8px 24px rgba(102,126,234,0.35)', opacity: busy ? 0.7 : 1 }}
        >
          {busy ? 'Please wait…' : `Subscribe ${selected === 'yearly' ? fmt(prices.yearly) + '/yr' : fmt(prices.monthly) + '/mo'}`}
        </button>
      </div>

      {/* Feature comparison */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px' }}>
        <div style={{ padding: '24px', backgroundColor: 'white', borderRadius: '16px', border: '1px solid #e5e7eb' }}>
          <h3 style={{ margin: '0 0 14px 0', fontSize: '1.1rem', color: '#374151' }}>Free</h3>
          {FREE_FEATURES.map(f => (
            <div key={f} style={{ display: 'flex', gap: '8px', padding: '6px 0', fontSize: '0.9rem', color: '#4b5563' }}>
              <span style={{ color: '#10b981' }}>✓</span> {f}
            </div>
          ))}
        </div>
        <div style={{ padding: '24px', backgroundColor: '#faf5ff', borderRadius: '16px', border: '2px solid #c4b5fd' }}>
          <h3 style={{ margin: '0 0 14px 0', fontSize: '1.1rem', color: '#6d28d9' }}>Premium ✨</h3>
          {PREMIUM_FEATURES.map(f => (
            <div key={f.title} style={{ display: 'flex', gap: '10px', padding: '8px 0' }}>
              <span style={{ fontSize: '1.2rem' }}>{f.icon}</span>
              <div>
                <div style={{ fontSize: '0.9rem', fontWeight: 600, color: '#1f2937' }}>{f.title}</div>
                <div style={{ fontSize: '0.78rem', color: '#6b7280' }}>{f.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default UpgradePage;
